/**
 * A preparação do pacote do caso (E168.F7, GUS-180), lida ao montar, relida enquanto o sistema
 * prepara e relida quando a página relê o caso.
 *
 * A próxima consulta só é agendada depois que a anterior volta — nunca empilha pedidos
 * (E113.F1.T03) —, e uma falha de releitura reagenda a seguinte em vez de parar a espera. A
 * retomada devolve o pacote já com a preparação em curso, e a espera recomeça por ele.
 *
 * E172.F19: `atualizar` relê sem voltar a "carregando" — o download que encontrou a prévia em
 * atualização pede o card de novo, que passa a mostrar a renovação e a esperar por ela.
 */
import type { AxiosInstance } from 'axios';
import { useCallback, useEffect, useState } from 'react';

import {
  lerPacoteDoCaso,
  PacoteDoCasoError,
  precisaReler,
  retomarSaidaDoPacote,
  RetomadaDoPacoteError,
  type MotivoDaFalhaDoPacote,
  type PacoteDoCasoData,
  type RecusaDaRetomada,
  type RetomadaDoPacoteData,
} from '@/lib/preparacao_do_pacote';

export const INTERVALO_DO_PACOTE_MS = 4000;

export type LeituraDoPacote =
  | { estado: 'carregando' }
  | { estado: 'erro'; motivo: MotivoDaFalhaDoPacote }
  | { estado: 'pronto'; dados: PacoteDoCasoData };

export type DesfechoDaRetomada = { aceita: true } | { aceita: false; recusa: RecusaDaRetomada };

export interface PreparacaoDoPacote {
  leitura: LeituraDoPacote;
  reler: () => void;
  atualizar: () => void;
  retomar: (retomada: RetomadaDoPacoteData) => Promise<DesfechoDaRetomada>;
}

function falha(err: unknown): MotivoDaFalhaDoPacote {
  return err instanceof PacoteDoCasoError ? err.motivo : 'indisponivel';
}

export function usePreparacaoDoPacote(
  client: AxiosInstance,
  caseId: string,
  releitura?: unknown,
): PreparacaoDoPacote {
  const [leitura, setLeitura] = useState<LeituraDoPacote>({ estado: 'carregando' });
  const [pedido, setPedido] = useState(0);
  const [tentativa, setTentativa] = useState(0);

  useEffect(() => {
    let cancelado = false;
    void lerPacoteDoCaso(client, caseId)
      .then((dados) => {
        if (!cancelado) setLeitura({ estado: 'pronto', dados });
      })
      .catch((err: unknown) => {
        if (!cancelado) setLeitura({ estado: 'erro', motivo: falha(err) });
      });
    return () => {
      cancelado = true;
    };
  }, [client, caseId, releitura, pedido]);

  // Troca de caso: o card não mostra o pacote de outro caso enquanto lê o novo.
  useEffect(() => {
    setLeitura({ estado: 'carregando' });
  }, [caseId]);

  const dados = leitura.estado === 'pronto' ? leitura.dados : null;
  const esperando = precisaReler(dados);
  useEffect(() => {
    if (!esperando) return undefined;
    let cancelado = false;
    const agendada = window.setTimeout(() => {
      void lerPacoteDoCaso(client, caseId)
        .then((lidos) => {
          if (!cancelado) setLeitura({ estado: 'pronto', dados: lidos });
        })
        .catch(() => {
          if (!cancelado) setTentativa((n) => n + 1);
        });
    }, INTERVALO_DO_PACOTE_MS);
    return () => {
      cancelado = true;
      window.clearTimeout(agendada);
    };
  }, [client, caseId, dados, esperando, tentativa]);

  const reler = useCallback(() => {
    setLeitura({ estado: 'carregando' });
    setPedido((n) => n + 1);
  }, []);

  const atualizar = useCallback(() => setPedido((n) => n + 1), []);

  const retomar = useCallback(
    async (retomada: RetomadaDoPacoteData): Promise<DesfechoDaRetomada> => {
      try {
        const dados = await retomarSaidaDoPacote(client, caseId, retomada);
        setLeitura({ estado: 'pronto', dados });
        return { aceita: true };
      } catch (err) {
        const recusa = err instanceof RetomadaDoPacoteError ? err.recusa : 'indisponivel';
        // A saída já não está para retomar (outra pessoa retomou, ou a versão mudou): relê.
        if (recusa === 'nao_retomavel') setPedido((n) => n + 1);
        return { aceita: false, recusa };
      }
    },
    [client, caseId],
  );

  return { leitura, reler, atualizar, retomar };
}
